import React, { FC, useEffect, useState } from "react";
import { Placeholder } from "kanopi-components/Placeholder";
import { quoteFlowFields } from "kw/api/fragments";
import { RouteComponentProps, useNavigate } from "@reach/router";
import { useIsActionAllowed } from "kw/hooks/useIsActionAllowed";
import { useMatchParams } from "kanopi-components/hooks/useMatchParams";
import {
  QuestionFlowComponent,
  QuestionFlowTypeEnum,
} from "kw/components/QuestionFlow/QuestionFlowComponent";
import { QuoteState } from "kw/api/zeus";

type TPolicyUpdateFlowPageProps = {
  policyId: string;
  questionSetKey: string;
  isLegalPage: boolean;
};

export const PolicyUpdateFlowPage: FC<
  TPolicyUpdateFlowPageProps & RouteComponentProps
> = ({ isLegalPage = false, policyId, uri: prefix }) => {
  const { questionSetKey } = useMatchParams(`${prefix}/:questionSetKey`);
  const flow = isLegalPage ? "legal" : "details";
  const policyUpdateAllowedQuery = {
    policy: [
      {
        id: policyId,
      },
      {
        updateAllowed: true,
      },
    ],
  };
  const startPolicyUpdateQuery = {
    startPolicyUpdate: [
      {
        input: {
          policyId,
          flow,
          questionSetKey,
          create: isLegalPage ? true : false,
        },
      },
      {
        ...quoteFlowFields,
      },
    ],
  };
  const [currentMaxSet, setCurrentMaxSet] = useState({
    currentSet: 0,
    maxSets: 1,
  });
  const { queryIsActionAllowed, data } = useIsActionAllowed(
    policyUpdateAllowedQuery,
    "policy.updateAllowed"
  );
  const { isAllowed: isPolicyUpdateAllowed, loading: isCheckUpdateAllowedLoading } =
    data || {};

  const navigate = useNavigate();
  const onCurrentMaxSetChange = (currentSet, maxSets) => {
    const _totalSets = { ...currentMaxSet };
    _totalSets.currentSet = currentSet;
    _totalSets.maxSets = maxSets;
    setCurrentMaxSet(_totalSets);
  };
  const onQuestionFlowKnockout = (profile) => {
    if ([QuoteState.KNOCKOUT, QuoteState.REFERRED].includes(profile?.state)) {
      navigate(`/policies/${policyId}/update/referral`);
    }
  };

  useEffect(() => {
    queryIsActionAllowed();
  }, []);

  useEffect(() => {
    if (isPolicyUpdateAllowed === false && !isCheckUpdateAllowedLoading) {
      navigate(`/policies/${policyId}`);
    }
  }, [isPolicyUpdateAllowed, isCheckUpdateAllowedLoading]);

  return (
    <Placeholder
      loading={!data?.isPolicyUpdateAllowed && data?.isCheckUpdateAllowedLoading}
    >
      <QuestionFlowComponent
        isFlowLayout={false}
        actionFlowType={QuestionFlowTypeEnum.Update}
        prefix={prefix}
        finishedRoute={`/policies/${policyId}/update`}
        defaultBackLocation={`/policies/${policyId}/update/`}
        actionQuery={startPolicyUpdateQuery}
        questionFlowResponseKey="startPolicyUpdate"
        questionSetKey={questionSetKey}
        steps={[
          {
            label: "Your details",
            active: true,
            current: currentMaxSet.currentSet,
            total: currentMaxSet.maxSets,
          },
          { label: "Your quote" },
          { label: "Review and buy" },
        ]}
        onCurrentAndMaxSetChange={onCurrentMaxSetChange}
        onQuestionFlowKnockout={onQuestionFlowKnockout}
      />
    </Placeholder>
  );
};
